import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { PaginatorModule } from 'primeng/paginator';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { Observable, Subject, filter, firstValueFrom, switchMap, takeUntil, timer } from 'rxjs';
import { NARROW_LAYOUT_MEDIA, prefersNarrowLayout } from '../utils/layout.util';
import {
  Transcription,
  TranscriptionsService,
  UPLOAD_SPINNER_ROW_ID,
  isCheckpointFinalRecordingKey,
  isUploadSpinnerTableRow
} from './transcriptions.service';
import { clearLocalStorageForPausedTranscriptionDraft } from '../recording/recording-checkpoint.storage';
import { PatientsService } from '../services/patients.service';
import { PatientSummary } from '../models/patient.model';

interface FilterOption {
  label: string;
  value: string;
}

interface EditingPatientSuggestion extends PatientSummary {
  nameOnly: string;
}

@Component({
  selector: 'app-transcriptions',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    CardModule,
    ButtonModule,
    TableModule,
    TagModule,
    ProgressSpinnerModule,
    InputTextModule,
    AutoCompleteModule,
    PaginatorModule,
    ToastModule,
    DialogModule,
    SelectModule
  ],
  providers: [MessageService],
  templateUrl: './transcriptions.component.html',
  styleUrls: ['./transcriptions.component.scss']
})
export class TranscriptionsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  /**
   * After Stop, wait a minute before the first list poll (notes usually take longer), then every 30s.
   * The list status is what decides the row is done.
   */
  private readonly statusPollDelayAfterStopMs = 60_000;
  private readonly statusPollIntervalMs = 30_000;
  /** Coalesce refresh requests that arrive while a refresh is already in flight (so upload spinner can clear). */
  private pendingTranscriptionsRefreshCoalesce = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private transcriptionsService: TranscriptionsService,
    private patientsService: PatientsService,
    private messageService: MessageService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  // Make Math available in template
  Math = Math;

  // Data signals
  transcriptions = signal<Transcription[]>([]);
  filteredTranscriptions = signal<Transcription[]>([]);
  loading = signal(true);
  isRefreshing = signal(false);

  // Mobile paginated data
  mobilePaginatedTranscriptions = signal<Transcription[]>([]);

  // Search and filter signals
  searchTerm = signal('');
  selectedStatus = signal<string>('');
  selectedSpecialty = signal<string>('');
  
  // Template properties for dropdowns and search input
  searchTermValue: string = '';
  selectedStatusValue: string = '';
  selectedSpecialtyValue: string = '';

  // Mobile pagination properties
  mobileCurrentPage = signal(0);
  mobilePageSize = 10;
  readonly appointmentTypeOptions: string[] = [
    'Initial Consultation',
    'Follow-up Visit',
    'Emergency Visit',
    'Procedure',
    'Review'
  ];
  editingTranscriptionId: string | null = null;
  editingPatientName: string = '';
  editingPatientSuggestions: EditingPatientSuggestion[] = [];
  /** Loaded once per inline-edit session; filtered locally on each keystroke (no HTTP per key). */
  private editingPatientSummariesCached: EditingPatientSuggestion[] | null = null;
  private editingPatientSummariesPending: Promise<EditingPatientSuggestion[]> | null = null;
  /** Invalidates in-flight suggestion updates when the query advances before microtasks run. */
  private editPatientSearchSeq = 0;
  selectedEditingPatient: PatientSummary | null = null;
  editingAppointmentType: string = '';
  savingRowId: string | null = null;
  deletingRowId: string | null = null;

  deleteConfirmVisible = signal(false);
  pendingDeleteTranscription = signal<Transcription | null>(null);

  /** Only one of desktop table vs mobile list is in the DOM so inline `p-autoComplete` is not duplicated (two overlays looked like doubled suggestions). */
  isNarrowLayout = signal(false);
  private narrowLayoutMql?: MediaQueryList;
  private readonly narrowLayoutListener = (): void => {
    this.isNarrowLayout.set(prefersNarrowLayout());
  };

  /**
   * Top "Uploading recording…" shim: only while awaiting and the list does not yet reflect pipeline work
   * (same gates as {@link #maybeClearRecordingUploadSpinnerFromList}). Keeps the row-level in-progress
   * spinner from stacking with the upload banner.
   */
  readonly showRecordingUploadShim = computed(() => {
    if (!this.transcriptionsService.isRecordingUploadAwaitingActive()) {
      return false;
    }
    const sorted = this.transcriptions();
    const linked = this.transcriptionsService.getRecordingUploadSpinnerLinkedRow();
    const linkedMatch = linked != null && sorted.some((t) => String(t.id).trim() === linked);
    const scheduled = this.transcriptionsService.peekRecordingUploadClearScheduled();
    const pipelineVisible = sorted.some((t) => this.getDisplayStatus(t.status) === 'In Progress');
    return !linkedMatch && !(scheduled && pipelineVisible);
  });

  /** Desktop table: optional first-row upload spinner (see {@link #showRecordingUploadShim}). */
  readonly tableRowsForDisplay = computed(() => {
    const filtered = this.filteredTranscriptions();
    if (!this.showRecordingUploadShim()) {
      return filtered;
    }
    const spinnerRow = {
      id: UPLOAD_SPINNER_ROW_ID,
      date: '',
      specialty: '',
      duration: '',
      status: '',
      patientName: '',
      mrn: '',
      appointmentType: '',
      originalFileName: ''
    } as Transcription;
    return [spinnerRow, ...filtered];
  });

  // Statistics signals
  totalRecords = signal(0);
  thisMonthCount = signal(0);
  averageDuration = signal('00:00');
  specialtyStats = signal({
    generalNeurology: 0,
    epilepsy: 0,
    stroke: 0,
    headache: 0,
    movementDisorders: 0
  });
  statusStats = signal({
    completed: 0,
    needsReview: 0,
    inProgress: 0,
    failed: 0
  });

  // Filter options - only show the 4 valid statuses that are displayed to users
  // Transcribing and Analyzing are displayed as "In Progress", so they're not separate filter options
  statusOptions: FilterOption[] = [
    { label: 'All Status', value: '' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Needs Review', value: 'Needs Review' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Paused', value: 'Paused' },
    { label: 'Failed', value: 'Failed' }
  ];

  specialtyOptions: FilterOption[] = [
    { label: 'All Specialties', value: '' },
    { label: 'General Neurology', value: 'General Neurology' },
    { label: 'Epilepsy', value: 'Epilepsy' },
    { label: 'Stroke', value: 'Stroke' },
    { label: 'Headache', value: 'Headache' },
    { label: 'Movement Disorders', value: 'Movement Disorders' }
  ];

  private loadTranscriptionsData(): Observable<Transcription[]> {
    // Use the transcriptions service to get data from backend API
    return this.transcriptionsService.getAllTranscriptions().pipe(
      takeUntil(this.destroy$)
    );
  }

  ngOnInit(): void {
    // Read query parameters from route snapshot (initial load)
    const queryParams = this.route.snapshot.queryParams;
    
    // Set search term if provided
    if (queryParams['search']) {
      this.searchTerm.set(queryParams['search']);
      this.searchTermValue = queryParams['search'];
    }
    
    // Set status filter if provided
    if (queryParams['status']) {
      this.selectedStatus.set(queryParams['status']);
      this.selectedStatusValue = queryParams['status'];
    }
    
    // Load transcriptions (filters will be applied after data loads)
    this.loadTranscriptions();

    this.transcriptionsService.transcriptionsListRefreshRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshTranscriptions());

    this.startTranscriptionStatusPolling();
    this.primePatientSuggestions();
    if (isPlatformBrowser(this.platformId)) {
      this.narrowLayoutMql = window.matchMedia(NARROW_LAYOUT_MEDIA);
      this.isNarrowLayout.set(prefersNarrowLayout());
      this.narrowLayoutMql.addEventListener('change', this.narrowLayoutListener);
    }
  }

  private primePatientSuggestions(): void {
    this.patientsService
      .getPatientSummaries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: () => {} });
  }

  private loadTranscriptions(): void {
    this.loadTranscriptionsData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (transcriptions) =>
          this.applyTranscriptionsFromApi(transcriptions, {
            // Consume pending upload-spinner dismissal on first paint — avoids spinner sticking when finalize
            // completes during navigation before refresh subscriptions replay / polling fires.
            consumeUploadAwaiting:
              this.transcriptionsService.isRecordingUploadAwaitingActive() ||
              this.transcriptionsService.peekRecordingUploadClearScheduled(),
            endInitialLoad: true
          }),
        error: (err) => {
          console.error('Error loading transcriptions:', err);
          this.loading.set(false);
        }
      });
  }

  refreshTranscriptions(): void {
    if (this.isRefreshing()) {
      this.pendingTranscriptionsRefreshCoalesce = true;
      return;
    }

    this.isRefreshing.set(true);
    this.loadTranscriptionsData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (transcriptions) => {
          this.applyTranscriptionsFromApi(transcriptions, { consumeUploadAwaiting: true });
          this.isRefreshing.set(false);
          if (this.pendingTranscriptionsRefreshCoalesce) {
            this.pendingTranscriptionsRefreshCoalesce = false;
            this.refreshTranscriptions();
          }
        },
        error: (error) => {
          this.handleListFetchError(error, 'Error refreshing transcriptions');
          this.isRefreshing.set(false);
          if (this.pendingTranscriptionsRefreshCoalesce) {
            this.pendingTranscriptionsRefreshCoalesce = false;
            this.refreshTranscriptions();
          }
        }
      });
  }

  /**
   * Poll only when something may still change: active recording upload or any non-terminal row.
   * Stops hitting the API when the list is all Completed / Needs Review / Failed and no upload.
   */
  private startTranscriptionStatusPolling(): void {
    const firstPollMs =
      this.transcriptionsService.isRecordingUploadAwaitingActive() ||
      !!this.transcriptionsService.pendingRecordingVisit() ||
      !!this.transcriptionsService.notesBuildingVisitId()
        ? this.statusPollDelayAfterStopMs
        : this.statusPollIntervalMs;
    timer(firstPollMs, this.statusPollIntervalMs)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.loading()),
        filter(() => !this.isRefreshing()),
        filter(() => this.shouldAutoRefreshTranscriptionList()),
        switchMap(() => this.loadTranscriptionsData())
      )
      .subscribe({
        next: (transcriptions) =>
          this.applyTranscriptionsFromApi(transcriptions, { consumeUploadAwaiting: true }),
        error: (err) => this.handleListFetchError(err, 'Transcriptions status poll')
      });
  }

  private shouldAutoRefreshTranscriptionList(): boolean {
    if (this.transcriptionsService.isRecordingUploadAwaitingActive()) {
      return true;
    }
    return this.transcriptions().some((t) => !this.isTerminalTranscriptionRow(t.status));
  }

  private isTerminalTranscriptionRow(status: string | undefined): boolean {
    const display = this.getDisplayStatus(status ?? '');
    return (
      display === 'Completed' ||
      display === 'Needs Review' ||
      display === 'Failed' ||
      display === 'Paused'
    );
  }

  private sortTranscriptionsByDateTime(transcriptions: Transcription[]): Transcription[] {
    return [...transcriptions].sort((a, b) => {
      const dateTimeA = a.appointmentTime
        ? a.appointmentTime instanceof Date
          ? a.appointmentTime
          : new Date(a.appointmentTime)
        : new Date(a.date);
      const dateTimeB = b.appointmentTime
        ? b.appointmentTime instanceof Date
          ? b.appointmentTime
          : new Date(b.appointmentTime)
        : new Date(b.date);
      return dateTimeB.getTime() - dateTimeA.getTime();
    });
  }

  private applyTranscriptionsFromApi(
    transcriptions: Transcription[],
    options: { consumeUploadAwaiting: boolean; endInitialLoad?: boolean }
  ): void {
    const sorted = this.sortTranscriptionsByDateTime(transcriptions);
    this.transcriptions.set(sorted);
    if (options.endInitialLoad) {
      this.loading.set(false);
    }
    this.maybeClearRecordingUploadSpinnerFromList(sorted);
    if (options.consumeUploadAwaiting) {
      if (this.transcriptionsService.consumeClearRecordingUploadAwaitingAfterRefresh()) {
        this.transcriptionsService.setRecordingUploadAwaitingResult(false);
      }
    }
    this.rebuildListView();
  }

  /**
   * Hide upload spinner once list reflects pipeline activity — complements consume-clear which keys only off refresh timing.
   */
  private maybeClearRecordingUploadSpinnerFromList(sorted: Transcription[]): void {
    if (!this.transcriptionsService.isRecordingUploadAwaitingActive()) {
      return;
    }
    const linked = this.transcriptionsService.getRecordingUploadSpinnerLinkedRow();
    const scheduled = this.transcriptionsService.peekRecordingUploadClearScheduled();
    const linkedMatch =
      linked != null && sorted.some((t) => String(t.id).trim() === linked);
    const pipelineVisible = sorted.some(
      (t) => this.getDisplayStatus(t.status) === 'In Progress'
    );
    /*
     * Always drop the upload shim once the refreshed list shows pipeline work ("In Progress") while
     * post-upload-dismiss is scheduled. Previously branch (scheduled && pipelineVisible) required !linked —
     * but recording always sets linked with the transcription id; a brief API id/format mismatch meant
     * linkedMatch stayed false while a real pipeline row rendered → "Uploading…" plus In Progress.
     */
    if (linkedMatch || (scheduled && pipelineVisible)) {
      this.transcriptionsService.consumeClearRecordingUploadAwaitingAfterRefresh();
      this.transcriptionsService.setRecordingUploadAwaitingResult(false);
    }
  }

  private handleListFetchError(error: unknown, context: string): void {
    console.error(context + ':', error);
    if (this.transcriptionsService.consumeClearRecordingUploadAwaitingAfterRefresh()) {
      this.transcriptionsService.setRecordingUploadAwaitingResult(false);
    }
    this.rebuildListView();
  }

  private rebuildListView(): void {
    this.applyFilters();
    this.totalRecords.set(this.transcriptions().length);
    this.calculateStatistics();
  }

  private calculateStatistics(): void {
    const transcriptions = this.transcriptions();
    
    // Calculate this month count
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const thisMonth = transcriptions.filter(t => {
      const date = new Date(t.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    }).length;
    this.thisMonthCount.set(thisMonth);

    // Calculate average duration
    let totalSeconds = 0;
    let validDurations = 0;
    
    transcriptions.forEach(t => {
      if (t.duration && t.duration !== 'N/A' && t.duration !== 'Unknown') {
        const parts = t.duration.split(':').map(Number);
        if (parts.length === 3) {
          // HH:MM:SS format
          totalSeconds += parts[0] * 3600 + parts[1] * 60 + parts[2];
          validDurations++;
        } else if (parts.length === 2) {
          // MM:SS format
          totalSeconds += parts[0] * 60 + parts[1];
          validDurations++;
        }
      }
    });
    
    if (validDurations > 0) {
      const averageSeconds = Math.round(totalSeconds / validDurations);
      const hours = Math.floor(averageSeconds / 3600);
      const minutes = Math.floor((averageSeconds % 3600) / 60);
      const seconds = averageSeconds % 60;
      
      // Format as MM:SS or HH:MM:SS
      if (hours > 0) {
        this.averageDuration.set(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      } else {
        this.averageDuration.set(
          `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }
    } else {
      this.averageDuration.set('00:00');
    }

    // Calculate specialty stats
    const specialtyCounts = transcriptions.reduce((acc, t) => {
      acc[t.specialty] = (acc[t.specialty] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    this.specialtyStats.set({
      generalNeurology: specialtyCounts['General Neurology'] || 0,
      epilepsy: specialtyCounts['Epilepsy'] || 0,
      stroke: specialtyCounts['Stroke'] || 0,
      headache: specialtyCounts['Headache'] || 0,
      movementDisorders: specialtyCounts['Movement Disorders'] || 0
    });

    // Calculate status stats (use display labels so TRANSCRIBING, etc. map correctly)
    const statusCounts = transcriptions.reduce((acc, t) => {
      const display = this.getDisplayStatus(t.status);
      acc[display] = (acc[display] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    this.statusStats.set({
      completed: statusCounts['Completed'] || 0,
      needsReview: statusCounts['Needs Review'] || 0,
      inProgress: statusCounts['In Progress'] || 0,
      failed: statusCounts['Failed'] || 0
    });
  }

  onSearch(): void {
    this.applyFilters();
  }

  onSearchInput(event: any): void {
    const value = event.target.value;
    this.searchTerm.set(value);
    this.searchTermValue = value;
    this.applyFilters();
  }

  onStatusFilterModelChange(): void {
    this.selectedStatus.set(this.selectedStatusValue);
    this.applyFilters();
  }

  onSpecialtyFilterModelChange(): void {
    this.selectedSpecialty.set(this.selectedSpecialtyValue);
    this.applyFilters();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  onResetFilters(): void {
    // Clear all filters
    this.searchTerm.set('');
    this.searchTermValue = '';
    this.selectedStatus.set('');
    this.selectedStatusValue = '';
    this.selectedSpecialty.set('');
    this.selectedSpecialtyValue = '';
    
    this.mobileCurrentPage.set(0);
    this.rebuildListView();
    
    // Clear query parameters from URL
    this.router.navigate(['/v0/transcriptions'], {
      queryParams: {},
      replaceUrl: true
    });
  }

  private applyFilters(): void {
    let filtered = [...this.transcriptions()];

    // Apply search filter (searches patient name, MRN, or status)
    const searchTerm = this.searchTerm();
    if (searchTerm) {
      filtered = filtered.filter(t => {
        const searchLower = searchTerm.toLowerCase();
        return (
          t.patientName?.toLowerCase().includes(searchLower) ||
          t.mrn?.toLowerCase().includes(searchLower) ||
          t.patientId?.toLowerCase().includes(searchLower) ||
          this.getDisplayStatus(t.status).toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply status filter (case-insensitive)
    // Use getDisplayStatus to match displayed statuses (e.g., "Transcribing" and "Analyzing" both display as "In Progress")
    const selectedStatus = this.selectedStatus();
    if (selectedStatus) {
      filtered = filtered.filter(t => {
        const displayStatus = this.getDisplayStatus(t.status);
        return displayStatus.toLowerCase() === selectedStatus.toLowerCase();
      });
    }

    // Apply specialty filter (case-insensitive)
    const selectedSpecialty = this.selectedSpecialty();
    if (selectedSpecialty) {
      filtered = filtered.filter(t => 
        t.specialty?.toLowerCase() === selectedSpecialty.toLowerCase()
      );
    }

    // Sort filtered results by date and time (latest to oldest)
    const sortedFiltered = filtered.sort((a, b) => {
      // Use appointmentTime if available, otherwise fall back to date
      // Handle both string and Date types for appointmentTime
      const dateTimeA = a.appointmentTime ? 
        (a.appointmentTime instanceof Date ? a.appointmentTime : new Date(a.appointmentTime)) : 
        new Date(a.date);
      const dateTimeB = b.appointmentTime ? 
        (b.appointmentTime instanceof Date ? b.appointmentTime : new Date(b.appointmentTime)) : 
        new Date(b.date);
      return dateTimeB.getTime() - dateTimeA.getTime();
    });

    this.filteredTranscriptions.set(sortedFiltered);
    this.updateMobilePagination();
  }

  private updateMobilePagination(): void {
    const filtered = this.filteredTranscriptions();
    const startIndex = this.mobileCurrentPage() * this.mobilePageSize;
    const endIndex = startIndex + this.mobilePageSize;
    const paginated = filtered.slice(startIndex, endIndex);
    this.mobilePaginatedTranscriptions.set(paginated);
  }

  onMobilePageChange(event: any): void {
    this.mobileCurrentPage.set(event.page);
    this.updateMobilePagination();
  }

  getStatusSeverity(status: string): string {
    // Normalize status to handle case-insensitive matching
    const normalizedStatus = status ? status.toLowerCase() : '';
    
    switch (normalizedStatus) {
      case 'completed':
        return 'success';
      case 'needs review':
        return 'warning';
      case 'in progress':
      case 'transcribing':
      case 'transcribed':
      case 'analyzing':
        return 'info';
      case 'failed':
        return 'danger';
      case 'paused':
        return 'secondary';
      default:
        // Treat any unknown status as "in progress" for styling
        return 'info';
    }
  }

  getDisplayStatus(status: string): string {
    // Normalize status to handle case variations from backend
    const normalizedStatus = status ? status.toLowerCase() : '';
    
    // Map backend statuses to display-friendly names
    switch (normalizedStatus) {
      case 'completed':
        return 'Completed';
      case 'needs review':
        return 'Needs Review';
      case 'in progress':
      case 'transcribing':
      case 'transcribed':
      case 'analyzing':
        return 'In Progress';
      case 'failed':
        return 'Failed';
      case 'paused':
        return 'Paused';
      default:
        // Treat any unknown status as "In Progress"
        return 'In Progress';
    }
  }

  isUploadSpinnerRow(transcription: Transcription): boolean {
    return isUploadSpinnerTableRow(transcription);
  }

  /** Small spinner in the actions column (desktop) / third row (mobile) while status is in progress. */
  showStatusProgressSpinner(transcription: Transcription): boolean {
    if (isUploadSpinnerTableRow(transcription)) {
      return false;
    }
    if (this.getDisplayStatus(transcription.status) !== 'In Progress') {
      return false;
    }
    if (this.showRecordingUploadShim()) {
      return false;
    }
    return true;
  }

  canOpenTranscription(transcription: Transcription): boolean {
    if (isUploadSpinnerTableRow(transcription)) {
      return false;
    }
    if (this.editingTranscriptionId === transcription.id || this.savingRowId === transcription.id) {
      return false;
    }
    const display = this.getDisplayStatus(transcription.status);
    if (display === 'Paused' || display === 'Failed') {
      return false;
    }
    return display === 'Completed' || display === 'Needs Review';
  }

  isPausedTranscription(transcription: Transcription): boolean {
    return this.getDisplayStatus(transcription.status) === 'Paused';
  }

  goResumePausedTranscription(transcription: Transcription, event: Event): void {
    event.stopPropagation();
    if (!this.isPausedTranscription(transcription)) {
      return;
    }
    void this.router.navigate(['/v0/recording'], {
      queryParams: { resumeTranscription: transcription.id }
    });
  }

  isAppendMoreRecordingEligible(transcription: Transcription): boolean {
    if (isUploadSpinnerTableRow(transcription)) {
      return false;
    }
    const d = this.getDisplayStatus(transcription.status);
    if (d !== 'Completed' && d !== 'Needs Review' && d !== 'Failed') {
      return false;
    }
    return isCheckpointFinalRecordingKey(transcription.originalFileName);
  }

  goAppendMoreRecording(transcription: Transcription, event: Event): void {
    event.stopPropagation();
    if (!this.isAppendMoreRecordingEligible(transcription)) {
      return;
    }
    void this.router.navigate(['/v0/recording'], {
      queryParams: { appendTranscription: transcription.id }
    });
  }

  onDeleteTranscription(transcription: Transcription, event: Event): void {
    event.stopPropagation();
    if (isUploadSpinnerTableRow(transcription) || this.deletingRowId) {
      return;
    }
    if (this.isEditingRow(transcription) || this.isSavingRow(transcription)) {
      return;
    }
    this.pendingDeleteTranscription.set(transcription);
    this.deleteConfirmVisible.set(true);
  }

  deleteConfirmPatientLabel(): string {
    const t = this.pendingDeleteTranscription();
    return (t?.patientName ?? '').trim() || 'this visit';
  }

  onDeleteConfirmVisibleChange(visible: boolean): void {
    this.deleteConfirmVisible.set(visible);
    if (!visible) {
      this.pendingDeleteTranscription.set(null);
    }
  }

  cancelDeleteConfirm(): void {
    this.deleteConfirmVisible.set(false);
  }

  confirmDeleteTranscription(): void {
    const transcription = this.pendingDeleteTranscription();
    if (!transcription || this.deletingRowId) {
      return;
    }
    this.deleteConfirmVisible.set(false);
    this.runDeleteTranscription(transcription);
  }

  private runDeleteTranscription(transcription: Transcription): void {
    this.deletingRowId = transcription.id;
    this.transcriptionsService
      .deleteTranscription(transcription.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deletingRowId = null;
          clearLocalStorageForPausedTranscriptionDraft(transcription.originalFileName);
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: 'Transcription was removed.'
          });
          this.refreshTranscriptions();
        },
        error: () => {
          this.deletingRowId = null;
          this.messageService.add({
            severity: 'error',
            summary: 'Delete failed',
            detail: 'Could not delete this transcription. Try again.'
          });
        }
      });
  }

  isDeletingRow(transcription: Transcription): boolean {
    return this.deletingRowId === transcription.id;
  }

  isEditingRow(transcription: Transcription): boolean {
    return this.editingTranscriptionId === transcription.id;
  }

  isSavingRow(transcription: Transcription): boolean {
    return this.savingRowId === transcription.id;
  }

  isUnknownPatient(transcription: Transcription): boolean {
    const name = (transcription.patientName ?? '').trim().toLowerCase();
    return name.includes('unknown');
  }

  startRowEdit(transcription: Transcription, event?: Event): void {
    event?.stopPropagation();
    if (isUploadSpinnerTableRow(transcription)) {
      return;
    }
    this.editingTranscriptionId = transcription.id;
    const currentName = (transcription.patientName ?? '').trim();
    this.editingPatientName = this.isUnknownPatient(transcription) ? '' : currentName;
    this.selectedEditingPatient = null;
    this.editingPatientSuggestions = [];
    this.clearEditingPatientSummariesCache();
    this.editingAppointmentType = (transcription.appointmentType ?? '').trim() || this.appointmentTypeOptions[0];
    void this.loadEditingPatientSummariesOnce().catch(() => {});
  }

  cancelRowEdit(event?: Event): void {
    event?.stopPropagation();
    this.editPatientSearchSeq++;
    this.editingTranscriptionId = null;
    this.editingPatientName = '';
    this.selectedEditingPatient = null;
    this.editingPatientSuggestions = [];
    this.editingAppointmentType = '';
    this.clearEditingPatientSummariesCache();
  }

  private clearEditingPatientSummariesCache(): void {
    this.editingPatientSummariesCached = null;
    this.editingPatientSummariesPending = null;
  }

  /** Single-flight fetch per edit session; subsequent calls reuse cache or the same promise. */
  private loadEditingPatientSummariesOnce(): Promise<EditingPatientSuggestion[]> {
    if (this.editingPatientSummariesCached) {
      return Promise.resolve(this.editingPatientSummariesCached);
    }
    if (this.editingPatientSummariesPending) {
      return this.editingPatientSummariesPending;
    }
    this.editingPatientSummariesPending = firstValueFrom(
      this.patientsService.getPatientSummaries().pipe(takeUntil(this.destroy$))
    )
      .then((patients) => {
        const list = this.buildEditingPatientSuggestionsFromApi(patients);
        this.editingPatientSummariesCached = list;
        this.editingPatientSummariesPending = null;
        return list;
      })
      .catch((err) => {
        this.editingPatientSummariesPending = null;
        throw err;
      });
    return this.editingPatientSummariesPending;
  }

  private applyEditingPatientSuggestionsForQuery(rawQuery: string, list: EditingPatientSuggestion[]): void {
    const query = (rawQuery ?? '').toString().toLowerCase().trim();
    if (!query) {
      this.editingPatientSuggestions = list.slice(0, 10);
      return;
    }
    this.editingPatientSuggestions = list
      .filter((p) => this.patientSuggestionMatchesQuery(p, query))
      .slice(0, 10);
  }

  /**
   * One row per patient id; removes exact duplicate payloads from the API.
   */
  private dedupePatientSuggestionsById(list: EditingPatientSuggestion[]): EditingPatientSuggestion[] {
    const byId = new Map<string, EditingPatientSuggestion>();
    for (const p of list) {
      if (p?.id == null || Number.isNaN(Number(p.id))) {
        continue;
      }
      const key = String(Number(p.id));
      if (!byId.has(key)) {
        byId.set(key, p);
      }
    }
    const withId = [...byId.values()];
    const orphan = list.filter((p) => p?.id == null || Number.isNaN(Number(p.id)));
    return [...withId, ...orphan];
  }

  /**
   * Exact duplicate identities (same name + MRN) collapse to one; see dedupePatientSuggestionsById.
   */
  private dedupePatientSuggestionsByNameAndMrn(list: EditingPatientSuggestion[]): EditingPatientSuggestion[] {
    const fingerprint = (p: EditingPatientSuggestion) =>
      [
        (p.firstName ?? '').trim().toLowerCase(),
        (p.lastName ?? '').trim().toLowerCase(),
        (p.mrn ?? '').trim().toLowerCase()
      ].join('\0');

    const seen = new Set<string>();
    const out: EditingPatientSuggestion[] = [];
    for (const p of list) {
      const fp = fingerprint(p);
      if (!fp.replace(/\u0000/g, '')) {
        continue;
      }
      if (seen.has(fp)) {
        continue;
      }
      seen.add(fp);
      out.push(p);
    }
    return out;
  }

  /**
   * PrimeNG shows {@code optionLabel} only — multiple distinct patients can share the same name.
   * When the trimmed name repeats, suffix MRN (or id) so every suggestion line is unique and meaningful.
   */
  private uniquifyPatientSuggestionLabels(list: EditingPatientSuggestion[]): EditingPatientSuggestion[] {
    const normNameKey = (p: EditingPatientSuggestion) =>
      `${(p.firstName ?? '').trim().toLowerCase()}\u0000${(p.lastName ?? '').trim().toLowerCase()}`;

    const groups = new Map<string, EditingPatientSuggestion[]>();
    for (const p of list) {
      const k = normNameKey(p);
      if (!k.replace(/\u0000/g, '')) {
        continue;
      }
      if (!groups.has(k)) {
        groups.set(k, []);
      }
      groups.get(k)!.push(p);
    }

    const out: EditingPatientSuggestion[] = [];
    for (const group of groups.values()) {
      const sorted = [...group].sort((a, b) => Number(a.id) - Number(b.id));
      const usedLabels = new Set<string>();
      for (const p of sorted) {
        const baseRaw = `${(p.firstName ?? '').trim()} ${(p.lastName ?? '').trim()}`.trim();
        let label = baseRaw;
        if (sorted.length > 1) {
          const mrn = (p.mrn ?? '').trim();
          label = mrn.length ? `${baseRaw} (${mrn})` : `${baseRaw} (#${p.id})`;
          if (usedLabels.has(label)) {
            label = `${baseRaw} (#${p.id})`;
          }
        }
        usedLabels.add(label);
        out.push({ ...p, nameOnly: label, displayName: label });
      }
    }
    return out;
  }

  private patientSuggestionMatchesQuery(p: EditingPatientSuggestion, query: string): boolean {
    const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
    const mrn = (p.mrn ?? '').toLowerCase();
    const label = (p.nameOnly ?? '').toLowerCase();
    const displayHay = `${p.displayName ?? ''} ${label}`.toLowerCase();
    return (
      fullName.includes(query) ||
      mrn.includes(query) ||
      label.includes(query) ||
      displayHay.includes(query)
    );
  }

  private buildEditingPatientSuggestionsFromApi(patients: PatientSummary[]): EditingPatientSuggestion[] {
    const mapped = patients.map((p) => ({
      ...p,
      displayName: p.displayName ?? `${p.firstName} ${p.lastName}`.trim(),
      nameOnly: `${p.firstName} ${p.lastName}`.trim()
    }));
    let next = this.dedupePatientSuggestionsById(mapped);
    next = this.dedupePatientSuggestionsByNameAndMrn(next);
    next = this.uniquifyPatientSuggestionLabels(next);
    return next.sort((a, b) =>
      `${a.firstName ?? ''} ${a.lastName ?? ''}`
        .trim()
        .localeCompare(`${b.firstName ?? ''} ${b.lastName ?? ''}`.trim(), undefined, {
          sensitivity: 'base'
        })
    );
  }

  onEditPatientSearch(event: { query: string }): void {
    const rawQuery = (event?.query ?? '').toString();
    const seq = ++this.editPatientSearchSeq;

    void this.loadEditingPatientSummariesOnce()
      .then((list) => {
        queueMicrotask(() => {
          if (seq !== this.editPatientSearchSeq) {
            return;
          }
          this.applyEditingPatientSuggestionsForQuery(rawQuery, list);
        });
      })
      .catch(() => {
        queueMicrotask(() => {
          if (seq !== this.editPatientSearchSeq) {
            return;
          }
          this.editingPatientSuggestions = [];
        });
      });
  }

  onEditPatientSelect(patient: PatientSummary | null): void {
    this.selectedEditingPatient = patient;
    if (patient) {
      this.editingPatientName = `${patient.firstName} ${patient.lastName}`.trim();
    }
  }

  private getEditingPatientNameString(): string {
    return this.editingPatientName.trim();
  }

  private resolveMetadataPatientId(): number | undefined {
    return this.selectedEditingPatient?.id;
  }

  onEditPatientNameChange(): void {
    const p = this.selectedEditingPatient;
    if (!p) {
      return;
    }
    const typed = this.getEditingPatientNameString().toLowerCase();
    const fullName = `${p.firstName} ${p.lastName}`.trim().toLowerCase();
    if (typed !== fullName) {
      this.selectedEditingPatient = null;
    }
  }

  saveRowEdit(transcription: Transcription, event?: Event): void {
    event?.stopPropagation();
    if (!this.isEditingRow(transcription) || this.isSavingRow(transcription)) {
      return;
    }

    const patientName = this.getEditingPatientNameString();
    const appointmentType = this.editingAppointmentType.trim();
    if (!patientName || !appointmentType) {
      return;
    }

    this.savingRowId = transcription.id;
    this.transcriptionsService
      .updateTranscriptionMetadata(transcription.id, {
        patientName,
        appointmentType,
        patientId: this.resolveMetadataPatientId()
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savingRowId = null;
          this.cancelRowEdit();
          this.refreshTranscriptions();
        },
        error: (err) => {
          console.error('Failed to update transcription metadata:', err);
          this.savingRowId = null;
        }
      });
  }

  onViewTranscription(transcriptionId: string, event?: Event): void {
    event?.stopPropagation();
    if (transcriptionId === UPLOAD_SPINNER_ROW_ID) {
      return;
    }
    const transcription = this.transcriptions().find((t) => t.id === transcriptionId);
    if (!transcription || !this.canOpenTranscription(transcription)) {
      return;
    }
    this.router.navigate(['/v0/transcriptions', transcriptionId]);
  }

  onPatientClick(patientId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['/v0/patients', patientId]);
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch (error) {
      return dateString;
    }
  }

  formatDateTime(transcription: any): string {
    // Use appointmentTime if available, otherwise fall back to date
    const dateTime = transcription.appointmentTime || transcription.date;
    if (!dateTime) return 'N/A';
    
    try {
      let date: Date;
      if (dateTime instanceof Date) {
        date = dateTime;
      } else {
        date = new Date(dateTime);
      }
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      
      // Convert to 12-hour format with AM/PM (using non-breaking space to prevent wrapping)
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      const hoursStr = hours.toString();
      
      // Use non-breaking space to prevent AM/PM from wrapping to next line
      return `${month}-${day}-${year} ${hoursStr}:${minutes}\u00A0${ampm}`;
    } catch (error) {
      return dateTime.toString();
    }
  }

  ngOnDestroy(): void {
    this.narrowLayoutMql?.removeEventListener('change', this.narrowLayoutListener);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
